import React from "react";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { PageHeader } from "../components/PageHeader";
import heroImage from "../assets/kaleidico-3V8xo5Gbusk-unsplash.jpg";
import { Button } from "primereact/button";
import { Carousel } from "primereact/carousel";
import { Link } from "react-router";

export const AboutPage: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <PageHeader
        title="About Logic Innova"
        description="Empowering businesses through innovative technology solutions and strategic digital transformation"
        backgroundImage={heroImage}
        height="500px"
        buttons={[
          {
            label: "Join Our Team",
            icon: "pi pi-users",
          },
        ]}
      />

      {/* Our Story Section */}
      <br />
      <br />
      <div
        id="our-story"
        className="container mx-auto px-8 py-32 md:py-40 mb-8 md:mb-16"
      >
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
            Our Story
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-loose">
            Founded with a vision to bridge the gap between business needs and
            technology solutions, Logic Innova has grown into a trusted partner
            for organizations seeking digital transformation. Our journey began
            with a simple mission: make technology work for people, not the
            other way around.
          </p>
        </div>
      </div>

      <Divider className="my-48 md:my-64 opacity-0" />

      {/* Our Journey Grid Section */}
      <div
        id="our-journey"
        className="bg-gray-50 dark:bg-gray-900 py-32 md:py-40 mb-8 md:mb-16"
      >
        <div className="container mx-auto px-8 md:px-12">
          <h2 className="text-5xl md:text-6xl font-bold text-center mb-20 tracking-tight text-gray-900 dark:text-white">
            Our Journey
          </h2>

          <div className="w-full max-w-[80%] mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 relative">
            {/* ROW 1: Card (col 1-2) */}
            <div className="md:col-span-2 p-8 bg-blue-50 dark:bg-gray-800 rounded-xl shadow-lg border-l-4 border-blue-500">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                    <i className="pi pi-flag text-white text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Foundation
                      </h3>
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                        2021
                      </span>
                    </div>
                    <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                      Starting our journey with a clear vision: to transform how
                      businesses leverage technology. We laid the foundation for
                      what would become a trusted partner in digital
                      transformation. Logic Innova was founded with a vision to
                      transform how businesses leverage technology.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ROW 2: Text (col 1) + Card (col 2-3) */}
            <div className="md:col-span-2 p-8 bg-green-50 dark:bg-gray-800 rounded-xl shadow-lg border-r-4 border-green-500">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <i className="pi pi-chart-line text-white text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Growth
                      </h3>
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        2022
                      </span>
                    </div>
                    <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                      Rapid expansion marked this year as we assembled a
                      world-class team of developers, designers, and
                      strategists. Our portfolio diversified across multiple
                      industries. Expanded our services and built a talented
                      team of technology experts.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ROW 3: Card (col 1-2) */}
            <div className="md:col-span-2 p-8 bg-purple-50 dark:bg-gray-800 rounded-xl shadow-lg border-l-4 border-purple-500">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                    <i className="pi pi-lightbulb text-white text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Innovation
                      </h3>
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                        2023
                      </span>
                    </div>
                    <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                      We launched cutting-edge solutions that pushed the
                      boundaries of what's possible. Strategic partnerships
                      opened new opportunities for innovation and collaboration.
                      Launched cutting-edge solutions and established strategic
                      partnerships.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ROW 4: Text (col 1) + Card (col 2-3) */}
            <div className="md:col-span-2 p-8 bg-green-50 dark:bg-gray-800 rounded-xl shadow-lg border-r-4 border-green-500">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <i className="pi pi-chart-line text-white text-2xl"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Excellence
                      </h3>
                      <span className="text-sm font-semibold px-3 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        2024
                      </span>
                    </div>
                    <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                      Recognition as industry leaders came through award-winning
                      projects and exceptional client satisfaction. Our
                      commitment to excellence continues to drive everything we
                      do.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Divider className="my-48 md:my-64 opacity-0" />

      {/* Our Delivery Process Carousel Section */}
      <div
        id="our-process"
        className="container mx-auto px-8 py-32 md:py-40 mb-8 md:mb-16"
      >
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-tight text-gray-900 dark:text-white">
            Our Delivery Process
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-loose">
            A proven methodology that ensures successful project outcomes from
            inception to deployment.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <Carousel
            value={[
              {
                step: 1,
                title: "Discovery & Planning",
                icon: "pi-search",
                color: "blue",
                description:
                  "We begin by understanding your business objectives, challenges, and requirements. Through collaborative workshops and stakeholder interviews, we define clear project goals and create a comprehensive roadmap.",
                highlights: [
                  "Requirements Analysis",
                  "Stakeholder Workshops",
                  "Project Roadmap",
                  "Technology Assessment",
                ],
              },
              {
                step: 2,
                title: "Design & Architecture",
                icon: "pi-pencil",
                color: "purple",
                description:
                  "Our expert team designs intuitive user experiences and robust system architecture. We create detailed wireframes, prototypes, and technical specifications that align with your vision.",
                highlights: [
                  "UX/UI Design",
                  "System Architecture",
                  "Prototyping",
                  "Technical Specifications",
                ],
              },
              {
                step: 3,
                title: "Development & Testing",
                icon: "pi-code",
                color: "green",
                description:
                  "Using agile methodologies, we build your solution with clean, maintainable code. Rigorous testing at every stage ensures quality, performance, and security standards are met.",
                highlights: [
                  "Agile Development",
                  "Code Reviews",
                  "Quality Assurance",
                  "Performance Testing",
                ],
              },
              {
                step: 4,
                title: "Deployment & Support",
                icon: "pi-check-circle",
                color: "orange",
                description:
                  "We ensure smooth deployment with minimal disruption to your operations. Our commitment continues with ongoing support, maintenance, and optimization to keep your solution running at peak performance.",
                highlights: [
                  "Seamless Deployment",
                  "User Training",
                  "Ongoing Support",
                  "Continuous Optimization",
                ],
              },
            ]}
            numVisible={1}
            numScroll={1}
            circular
            autoplayInterval={5000}
            itemTemplate={(item) => (
              <div className="p-8">
                <Card className="shadow-2xl border-0 bg-white dark:bg-gray-800">
                  <div className="text-center mb-10">
                    {/* Icon */}
                    <div
                      className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-6 shadow-lg"
                      style={{
                        backgroundColor:
                          item.color === "blue"
                            ? "#3B82F6"
                            : item.color === "purple"
                            ? "#8B5CF6"
                            : item.color === "green"
                            ? "#10B981"
                            : "#F59E0B",
                      }}
                    >
                      <i
                        className={`pi ${item.icon} text-3xl`}
                        style={{ color: "#FFFFFF" }}
                      ></i>
                    </div>

                    {/* Step Badge */}
                    <div className="mb-3">
                      <span
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-semibold text-sm"
                        style={{
                          backgroundColor:
                            item.color === "blue"
                              ? "rgba(59, 130, 246, 0.1)"
                              : item.color === "purple"
                              ? "rgba(139, 92, 246, 0.1)"
                              : item.color === "green"
                              ? "rgba(16, 185, 129, 0.1)"
                              : "rgba(245, 158, 11, 0.1)",
                          color:
                            item.color === "blue"
                              ? "#3B82F6"
                              : item.color === "purple"
                              ? "#8B5CF6"
                              : item.color === "green"
                              ? "#10B981"
                              : "#F59E0B",
                        }}
                      >
                        <span>Step {item.step}</span>
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                      {item.title}
                    </h3>
                  </div>

                  <p className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
                    {item.description}
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                    {item.highlights.map((highlight: string, index: number) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700"
                      >
                        <i className="pi pi-check text-green-500 text-sm"></i>
                        <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                          {highlight}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}
            className="delivery-process-carousel"
          />
        </div>
      </div>

      <Divider className="my-48 md:my-64 opacity-0" />

      {/* CTA Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 dark:from-blue-700 dark:via-blue-800 dark:to-indigo-900 py-32 md:py-40 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-white blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 rounded-full bg-white blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full bg-white blur-2xl"></div>
        </div>

        <div className="container mx-auto px-8 md:px-12 text-center relative z-10">
          {/* Icon Feature Row */}
          <div className="flex justify-center gap-8 md:gap-12 mb-12 md:mb-16 flex-wrap">
            <div className="flex items-center gap-2 text-white/90">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="pi pi-building text-white text-lg"></i>
              </div>
              <span className="text-sm font-medium">
                Established Excellence
              </span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="pi pi-globe text-white text-lg"></i>
              </div>
              <span className="text-sm font-medium">Global Reach</span>
            </div>
            <div className="flex items-center gap-2 text-white/90">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="pi pi-heart-fill text-white text-lg"></i>
              </div>
              <span className="text-sm font-medium">Client Satisfaction</span>
            </div>
          </div>

          <br />
          <br />

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tight">
            Ready to Work Together?
          </h2>
          <p className="text-xl md:text-2xl text-white mb-12 md:mb-16 max-w-3xl mx-auto leading-loose">
            Let's start a conversation about how Logic Innova can help your
            organization thrive in the digital age.
          </p>
          <div className="flex gap-5 justify-center flex-wrap">
            <Link to="/contact">
              <Button
                label="Get in Touch"
                icon="pi pi-envelope"
                iconPos="right"
                size="large"
                className="shadow-2xl hover:shadow-3xl transition-all hover:scale-105"
                style={{
                  gap: "0.5rem",
                  padding: "1rem 2rem",
                  fontSize: "1.1rem",
                }}
              />
            </Link>
            <Link to="/services">
              <Button
                label="View Services"
                icon="pi pi-arrow-right"
                iconPos="right"
                outlined
                size="large"
                className="shadow-xl hover:shadow-2xl transition-all hover:scale-105 hover:bg-white/10"
                style={{
                  color: "white",
                  borderColor: "white",
                  borderWidth: "2px",
                  gap: "0.5rem",
                  padding: "1rem 2rem",
                  fontSize: "1.1rem",
                }}
              />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};
